import { useEffect, useMemo } from 'react';
import CompleteActivityFields from './sections/fields';
import CompleteActivityObjects from './sections/relatedObjects';
import { EMPTY_ARRAY, EMPTY_OBJECT } from './config';
import { isEmptyValue } from './helpers';
import { useKeyListeners } from 'hooks/keyboardEventHandler/useKeyListeners';
import { KeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';

import { validateEmail } from 'components/Inputs/TextInput/presets/EmailAddress';
import { OBJECT_ASSOCATIONS } from 'components/GenericWizard/wizards/scheduleActivity';
import { isEmailField } from 'checks/fields';
import { useCompleteActivityWizardContext } from './context';

const CompleteActivitytWizard = ({
  onChange,
  data = EMPTY_OBJECT,
  showErrors,
  setShowErrors,
}) => {
  const {
    activity,
    activityFields,
    activityRelatedObjects,
    assignments,
    formData,
    isComplete,
    isDirty,
    isLoading,
    isLoadingActivityAssociations,
    loadActivityError,
    dispatchAssignment,
    isRuleActiveForField,
    updateField,
    updateValidation,
  } = useCompleteActivityWizardContext();

  const activityId = data.activityObject?.id;
  const associationMode = data.activityObject?.associationMode;

  const { fields, relatedObjects } = formData;

  const errors = useMemo(() => {
    if (isLoading || fields.length === 0) {
      return [];
    }

    const fieldsById = fields.reduce((acc, field) => {
      acc[field.id] = field;
      return acc;
    }, {});

    return activity.layout.map((item) => {
      if (item.type === 'section') {
        return false;
      }

      const field = fieldsById[item.fieldId];
      const activityField = activityFields.find((x) => x.id === item.fieldId);

      if (!activityField || !field) {
        return false;
      }

      if (isRuleActiveForField(field)) {
        if (isEmailField(field)) {
          return (
            activityField.isRequired && validateEmail(field.value) === null
          );
        } else {
          return activityField.isRequired && isEmptyValue(field.value);
        }
      }
      return false;
    }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, activity?.layout, activityFields, isLoading]); // isRuleActiveForField is not stable

  useEffect(() => {
    const data = {
      ...formData,
      fields: formData.fields.filter(isRuleActiveForField),
      passVariablesOnRedirect: activity?.passVariablesOnRedirect,
      redirectUrl: activity?.redirectUrl,
      redirectParameterFields: activity?.redirectParameterFields,
      submissionAction: activity?.submissionAction,
    };
    onChange({ data, isComplete, isDirty, errors });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, isComplete, isDirty, onChange, errors]);

  const fieldsData = useMemo(
    () => [
      ...activityRelatedObjects.map(({ id }) => ({ id })),
      ...fields.map(({ id }) => ({ id })),
    ],
    [activityRelatedObjects, fields]
  );

  const { assignFieldHandle, getKeyListenersProps } = useKeyListeners(
    fieldsData,
    {},
    () => true,
    true
  );

  return (
    <KeyBoardContext.Provider value={{ assignFieldHandle }}>
      <>
        {associationMode !== OBJECT_ASSOCATIONS.NO_OBJECT_ASSOCIATIONS ? (
          <CompleteActivityObjects
            getKeyListenersProps={getKeyListenersProps}
            dispatchAssignment={dispatchAssignment}
            assignments={assignments}
            updateField={updateField}
            updateValidation={updateValidation}
            activityRelatedObjects={
              loadActivityError ? EMPTY_ARRAY : activityRelatedObjects
            }
            relatedObjects={relatedObjects}
            loading={isLoadingActivityAssociations}
            setShowErrors={setShowErrors}
            isLastIndex
          />
        ) : null}
        <CompleteActivityFields
          getKeyListenersProps={getKeyListenersProps}
          associationMode={associationMode}
          activityId={activityId}
          errors={errors}
          showErrors={showErrors}
          setShowErrors={setShowErrors}
        />
      </>
    </KeyBoardContext.Provider>
  );
};

export default CompleteActivitytWizard;
